"use client";

import { useClerk } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { SIGN_OUT_TOAST_KEY } from "@/features/auth/components/sign-out-toast";
import { useCartStore } from "@/features/cart/components";
import { useMultidietCartStore } from "@/features/multidiet/components/multidiet-cart-provider";
import {
  getLocaleForPathname,
  getPathname,
  type LocalizedRoute,
  useRouter,
} from "@/i18n/navigation";

export const useResetSessionState = () => {
  const queryClient = useQueryClient();
  const resetCart = useCartStore((state) => state.resetCart);
  const resetMultidietCart = useMultidietCartStore((state) => state.resetCart);

  return useCallback(() => {
    resetCart();
    resetMultidietCart();
    queryClient.clear();
  }, [queryClient, resetCart, resetMultidietCart]);
};

export const useSignOut = ({ href = "/" }: { href?: LocalizedRoute } = {}) => {
  const { signOut } = useClerk();
  const router = useRouter();
  const locale = getLocaleForPathname();
  const resetSessionState = useResetSessionState();

  return useCallback(async () => {
    // Cleared before `signOut`, because the redirect may run before anything
    // after the call gets a chance to.
    resetSessionState();
    sessionStorage.setItem(
      SIGN_OUT_TOAST_KEY,
      typeof href === "string" ? href : href.pathname,
    );

    // The redirect is ours rather than Clerk's `redirectUrl`: the drawer
    // unmounts its own sign-out button while signing out, so the navigation
    // must not depend on the caller still being mounted. Whichever page the
    // sign-out started from, it ends on `href` — the home page by default.
    await signOut();
    router.replace(getPathname({ href, locale }));
  }, [href, locale, resetSessionState, router, signOut]);
};
